import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import moment from "moment";
import "moment/locale/fr-ca";
import { enResources } from "./resources";
import { rememberLanguage } from "./preference";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  LANGUAGE_STORAGE_KEY,
  LANGUAGES,
  NAMESPACES,
  toLanguage,
} from "./constants";

i18n
  .use(LanguageDetector)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./${language}/${namespace}.json`),
    ),
  )
  .use(initReactI18next)
  .init({
    resources: { en: enResources },
    // Lets the backend fill in `fr`/`iu` while `en` stays bundled.
    partialBundledLanguages: true,
    supportedLngs: LANGUAGES,
    // fr-CA from the browser resolves to `fr`.
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    fallbackLng: DEFAULT_LANGUAGE,
    // Every namespace loads up front, so switching pages never suspends.
    ns: NAMESPACES,
    defaultNS: DEFAULT_NAMESPACE,
    detection: {
      order: ["querystring", "localStorage", "navigator"],
      lookupQuerystring: "lng",
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // Detected languages aren't cached; see preference.ts.
      caches: [],
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });

// A `?lng` link counts as a pick, so it sticks after the param is gone.
const linked = new URLSearchParams(window.location.search).get("lng");
if (linked && LANGUAGES.some((language) => linked.startsWith(language))) {
  rememberLanguage(toLanguage(linked));
}

// Moment formats the calendars and meeting times, so its day and month names
// follow the app language too. Inuktitut dates stay in English.
const syncLocale = (code: string) => {
  const language = toLanguage(code);
  document.documentElement.lang = language;
  moment.locale(language === "fr" ? "fr-ca" : "en");
};

syncLocale(i18n.language);
i18n.on("languageChanged", syncLocale);

export default i18n;
